import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import type { PackageFile, PackageResponse, UpdatePackageRequest } from "./packagesApi";
import { addPackageAttachment, deletePackage, deletePackageAttachment, updatePackage } from "./packagesApi";
import { usePackageWithId } from "./usePackageWithId";

export const usePackage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { packageId } = useParams<{ packageId: string }>();
  const { data, isLoading } = usePackageWithId(packageId);

  const updatePackageMutation = useMutation<PackageResponse, unknown, UpdatePackageRequest>({
    mutationFn: (v) => updatePackage(packageId!, v),
    onSuccess: async (data) => {
      enqueueSnackbar(`Pakiet ${data.name.value} został zaktualizowany`, { variant: "success" });
      queryClient.setQueryData(["packages", packageId], data);
      await queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować pakietu. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const deletePackageMutation = useMutation<void, unknown, { packageId: string }>({
    mutationFn: ({ packageId }) => deletePackage(packageId),
    onSuccess: async () => {
      enqueueSnackbar("Pakiet został usunięty", { variant: "success" });
      queryClient.removeQueries({ queryKey: ["packages", packageId] });
      await queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
  });

  const addPackageAttachmentMutation = useMutation<PackageFile, unknown, FormData>({
    mutationFn: (formData) => addPackageAttachment(packageId!, formData),
    onSuccess: async () => {
      enqueueSnackbar("Załącznik został dodany", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["packages", packageId] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać załącznika. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const deletePackageAttachmentMutation = useMutation<PackageFile[], unknown, { name: string }>({
    mutationFn: ({ name }) => deletePackageAttachment(packageId!, name),
    onSuccess: async () => {
      enqueueSnackbar("Załącznik został usunięty", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["packages", packageId] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć załącznika. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  return {
    package: data,
    isLoading,
    deletePackage: (packageId: string) => deletePackageMutation.mutateAsync({ packageId }),
    updatePackage: (update: UpdatePackageRequest) => updatePackageMutation.mutateAsync(update),
    addAttachment: (formData: FormData) => addPackageAttachmentMutation.mutateAsync(formData),
    deleteAttachment: (name: string) => deletePackageAttachmentMutation.mutateAsync({ name }),
  };
};
